import { useRequest } from 'ahooks'
import { Button } from 'antd'
import { isEmpty } from 'lodash'

import { checkPhoneExistsApi } from '@eastcoal/api/auth/register/checkPhoneExists'
import { registerApi } from '@eastcoal/api/auth/register/register'

import { useFeedback } from '@eastcoal/common/hooks/useFeedback'
import { pick } from '@eastcoal/common/utils/object'

import { Checkbox } from '@eastcoal/formily/components/Checkbox'
import { FormItem } from '@eastcoal/formily/components/FormItem'
import { Input } from '@eastcoal/formily/components/Input'
import { Password } from '@eastcoal/formily/components/Password'
import { SchemaForm } from '@eastcoal/formily/components/SchemaForm'
import {
  FormilyValidator,
  SchemaItem
} from '@eastcoal/formily/components/SchemaForm/interface'
import { SmsInput } from '@eastcoal/formily/components/SmsInput'
import { useCreationForm } from '@eastcoal/formily/hooks/useCreationForm'
import { passwordStrengthValidator } from '@eastcoal/formily/rules/passwordStrengthRule'

import { SmsTemplateCode } from '@eastcoal/shared/enums/SmsTemplateCode'

import { UserAgreement } from '../../UserAgreement'
import { useRegisterStore } from '../hooks/useStore'
import { useRegisterStyles } from '../style'

const phoneExistsValidator: FormilyValidator = async (value, _, { form }) => {
  try {
    if (isEmpty(value)) {
      return true
    }
    // 校验手机号是否已注册，如果已注册则提前提示省得白填
    const exists = await checkPhoneExistsApi(
      { phone: value },
      { timeout: 3000 }
    )
    const valid = exists === false
    form.setFieldState('allowSendCode', state => {
      state.value = valid
    })
    return valid
  } catch {
    // 请求发送失败时允许用户发送验证码，然后在注册时再次校验
    form.setFieldState('allowSendCode', state => {
      state.value = true
    })
    return true
  }
}
const schema: SchemaItem[] = [
  {
    type: 'string',
    name: 'phone',
    'x-decorator': FormItem,
    'x-component': Input,
    'x-component-props': {
      placeholder: '请填写绑定的手机号'
    },
    'x-validator': [
      {
        required: true,
        triggerType: 'onBlur',
        message: '请填写手机号'
      },
      {
        triggerType: 'onBlur',
        format: 'phone',
        message: '请填写有效的国内号码'
      },
      {
        triggerType: 'onBlur',
        message: '手机号已被注册',
        validator: phoneExistsValidator
      }
    ]
  },
  {
    type: 'boolean',
    name: 'allowSendCode',
    'x-display': 'hidden'
  },
  {
    type: 'object',
    name: '{ticketId,code}',
    'x-decorator': FormItem,
    'x-component': SmsInput,
    'x-component-props': {
      captchaEnabled: true,
      templateCode: SmsTemplateCode.REGISTER,
      phone: '{{ $values.phone }}',
      comment: '注册',
      allowSend: '{{ !!$values.allowSendCode }}'
    },
    'x-validator': {
      required: true,
      message: '请输入验证码'
    }
  },
  {
    type: 'string',
    name: 'password',
    'x-decorator': FormItem,
    'x-component': Password,
    'x-component-props': {
      placeholder: '请设置登录密码',
      checkStrength: true
    },
    'x-validator': [
      {
        required: true,
        message: '请填写登录密码'
      },
      {
        validator: passwordStrengthValidator
      }
    ]
  },
  {
    type: 'string',
    name: 'confirmPassword',
    'x-decorator': FormItem,
    'x-component': Password,
    'x-component-props': {
      placeholder: '请确认登录密码'
    },
    'x-validator': [
      {
        required: true,
        message: '请确认登陆密码'
      },
      {
        const: '{{ $values.password }}',
        message: '两次输入的密码不一致'
      }
    ]
  },
  {
    type: 'boolean',
    name: 'agreement',
    'x-decorator': FormItem,
    'x-component': Checkbox,
    'x-component-props': {
      children: (
        <>
          <span>我已阅读并同意东方煤炭电子交易中心</span>
          <UserAgreement />
        </>
      )
    }
  }
]

export const RegisterForm = () => {
  const { modal } = useFeedback()

  const { openId, setForm, gotoResult } = useRegisterStore(state =>
    pick(state, ['openId', 'setForm', 'gotoResult'])
  )
  const form = useCreationForm({
    validateFirst: true
  })

  const { loading, runAsync } = useRequest(registerApi, {
    manual: true
  })

  const handleRegister = () => {
    form.submit(async values => {
      if (values.agreement !== true) {
        modal.confirm({
          title: '服务协议及隐私保护',
          content: (
            <>
              <span>为更好地保护你的合法权益，请阅读并同意</span>
              <UserAgreement />
            </>
          ),
          okText: '同意',
          cancelText: '不同意',
          onOk: () => {
            form.setFieldState('agreement', {
              value: true
            })
            handleRegister()
          }
        })
      } else {
        const data = { ...values, openId }
        await runAsync(data)
        setForm(data)
        gotoResult()
      }
    })
  }

  const { styles } = useRegisterStyles()

  return (
    <SchemaForm
      className={styles.form}
      form={form}
      name='registerForm'
      schema={schema}
      grid={{
        columns: 1
      }}
      onSubmit={handleRegister}
    >
      <Button block htmlType='submit' loading={loading} type='primary'>
        提交
      </Button>
    </SchemaForm>
  )
}
